import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import './MessageDetail.css';

interface Message {
  id: string;
  senderId: string;
  content: string;
  timestamp: Timestamp;
  type: 'text' | 'image';
  isRead: boolean;
  readAt?: Timestamp;
  // Additional fields for display
  senderName?: string;
}

interface User {
  name: string;
  email: string;
}

interface Conversation {
  id: string;
  conversationId: string;
  bookingId: string;
  providerId: string;
  customerId: string;
  isActive: boolean;
  createdAt: Timestamp;
  lastMessageAt: Timestamp;
  lastMessage: string | null;
  completedAt?: Timestamp;
  unreadCountCustomer: number;
  unreadCountProvider: number;
}

const MessageDetail: React.FC = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);

  useEffect(() => {
    if (bookingId) {
      fetchConversation();
    }
  }, [bookingId]);

  useEffect(() => {
    if (conversation) {
      fetchMessages();
    }
  }, [conversation]);

  const fetchConversation = async () => {
    try {
      const conversationsRef = collection(db, 'conversations');
      const q = query(conversationsRef, where('bookingId', '==', bookingId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const conversationDoc = querySnapshot.docs[0];
        const conversationData = {
          id: conversationDoc.id,
          ...conversationDoc.data()
        } as Conversation;
        
        setConversation(conversationData);
      } else {
        setError('Không tìm thấy cuộc hội thoại cho booking này');
        setLoading(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error fetching conversation:', errorMessage);
      setError('Không thể tải thông tin cuộc hội thoại');
      setLoading(false);
    }
  };

  const fetchUserData = async (userId: string): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data() as User;
      }
      return null;
    } catch (err) {
      console.error('Error fetching user data:', err);
      return null;
    }
  };

  const fetchMessages = async () => {
    if (!conversation) return;

    try {
      setLoading(true);

      // Get messages from the subcollection
      const messagesRef = collection(db, 'conversations', conversation.id, 'messages');
      const messagesQuery = query(
        messagesRef,
        orderBy('timestamp', 'asc')
      );

      const messagesSnapshot = await getDocs(messagesQuery);
      let messagesData: Message[] = [];

      if (!messagesSnapshot.empty) {
        messagesData = messagesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Message));

        // Fetch sender names for each message
        const messagesWithSenders = await Promise.all(
          messagesData.map(async (message) => {
            const sender = await fetchUserData(message.senderId);
            return {
              ...message,
              senderName: sender?.name || 'Không tìm thấy'
            };
          })
        );

        setMessages(messagesWithSenders);
      }

      setLoading(false);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error fetching messages:', errorMessage);
      setError('Không thể tải tin nhắn. Vui lòng thử lại sau.');
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    if (!timestamp || !timestamp.toDate) {
      return 'Không có thời gian';
    }
    try {
      return timestamp.toDate().toLocaleString('vi-VN');
    } catch (err) {
      console.error('Error formatting timestamp:', err);
      return 'Không có thời gian';
    }
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    if (conversation) {
      fetchMessages();
    } else if (bookingId) {
      fetchConversation();
    }
  };

  return (
    <div className="message-detail">
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Quay lại
        </button>
        <h2>Chi tiết tin nhắn - Booking {bookingId}</h2>
      </div>

      {loading && (
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải tin nhắn...</div>
        </div>
      )}

      {error && (
        <div className="error-container">
          <div className="error-message">
            <span>{error}</span>
            <button className="retry-button" onClick={handleRetry}>
              Thử lại
            </button>
          </div>
        </div>
      )}

      {!loading && !error && messages.length === 0 && (
        <div className="no-messages">
          <p>Chưa có tin nhắn nào trong cuộc hội thoại này</p>
        </div>
      )}

      <div className="messages-container">
        {messages.map((message) => (
          <div key={message.id} className="message-item">
            <div className="message-header">
              <span className="sender-name">{message.senderName}</span>
              <span className="message-time">{formatTimestamp(message.timestamp)}</span>
            </div>
            <div className="message-content">
              {message.type === 'image' ? (
                <img src={message.content} alt="Message attachment" className="message-image" />
              ) : (
                <p>{message.content}</p>
              )}
            </div>
            {message.isRead && (
              <div className="message-status">
                <small>Đã đọc: {message.readAt ? formatTimestamp(message.readAt) : 'N/A'}</small>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MessageDetail; 